import { isGeoPointValid } from '@hive/util'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { CustomerAddress, InitializeOrderQuery, OrderInfoInput, OrderRecipientInfoInput, useInitializeOrderQuery, useUpdateOrderInfoWithSlotCheckMutation } from '@shared/generated'
import { useDebounce } from '@shared/uttils/useDebounce'

import { usePlaceOrder } from './usePlaceOrder'

export type AssemblingDetailsInput = {
  comment: string
  assembly_method: number
}

type UsePendingOrderProps = {
  shopId: string;
  isAvailable?: boolean;
  onSlotChanged?: (prevSlot: string | number | null, newSlot: string | number) => void;
}

// TODO: Make common type instead!
export type RemotePendingOrderState = InitializeOrderQuery['initializeOrder']

export const usePendingOrder = ({ shopId, onSlotChanged, isAvailable = false }: UsePendingOrderProps) => {
  const { data, loading, refetch } = useInitializeOrderQuery({
    variables: {
      shopId
    },
    skip: !shopId || !isAvailable,
    fetchPolicy: 'network-only',
  })

  const [ pendingStateChanges, setPendingStateChanges ] = useState<Partial<OrderInfoInput>>({})
  const debouncedPendingStateChanges = useDebounce(pendingStateChanges, 3000)

  const [ updateOrderInfoWithSlotCheck, { loading: updateInProgress } ] = useUpdateOrderInfoWithSlotCheckMutation()

  const pendingOrder = data?.initializeOrder
  const pendingOrderId = pendingOrder?.id
  const prevSlotId = pendingOrder?.slot_id

  const placeOrderHandles = usePlaceOrder()

  const storeOrderChanges = useCallback(async (pendingState: Partial<OrderInfoInput>) => {
    if (Object.keys(pendingState).length > 0 && isAvailable) {
      const { data } = await updateOrderInfoWithSlotCheck({
        variables: {
          orderId: pendingOrderId as string,
          orderInfo: pendingState
        }
      })

      setPendingStateChanges({})

      if (data?.updateOrderInfoWithSlotCheck?.notifySlotChanged) {
        onSlotChanged?.(prevSlotId ?? null, data.updateOrderInfoWithSlotCheck.orderInfo?.slot_id ?? 0)
      }

      return data?.updateOrderInfoWithSlotCheck
    }
  },[ isAvailable, onSlotChanged, pendingOrderId, prevSlotId, updateOrderInfoWithSlotCheck ])

  // Here we store changes while they are incoming to state
  useEffect(() => {
    storeOrderChanges(debouncedPendingStateChanges)
  }, [ storeOrderChanges, debouncedPendingStateChanges ])

  const updateOrder = useCallback(async (orderInfo: Partial<OrderInfoInput>) => {
    if (!pendingOrderId) {
      return null
    }

    setPendingStateChanges(prev => ({
      ...prev,
      ...orderInfo
    }))
  }, [ pendingOrderId ])

  const updateAddress = useCallback(async (addressObject: Partial<CustomerAddress>, isPickup: boolean, zoneId?: string) => {
    const customerGeoPoint =
      isGeoPointValid(addressObject.customerGeoPoint)
        ? addressObject.customerGeoPoint
        : undefined

    return await updateOrder({
      // TODO: Pass deliveryType instead of isPickup hack!
      deliveryType: isPickup ? 'pickup' : 'slot',
      address: addressObject.address || '',
      address_appartments: addressObject.address_appartments,
      address_intercom: addressObject.address_intercom,
      address_floor: addressObject.address_floor,
      address_porch: addressObject.address_porch,
      customer_geoposition: customerGeoPoint,
      no_door_call: addressObject.no_door_call,
      door_delivery: addressObject.door_delivery,
      zone_id: zoneId
    })
  }, [ updateOrder ])

  const updateSlot = useCallback(async (slotId: number) => {
    return await updateOrder({
      slot_id: slotId
    })
  }, [ updateOrder ])

  const updateRecipientInfo = useCallback(async (recipientInfo: Partial<OrderRecipientInfoInput>) => {
    return await updateOrder({
      recipientInfo
    })
  }, [ updateOrder ])

  const updateCourierMessage = useCallback(async (courierMessage: string) => {
    return await updateOrder({
      customerCourierMessage: courierMessage
    })
  }, [ updateOrder ])

  const updateAssemblingDetails = useCallback(async (assemblingDetails: Partial<AssemblingDetailsInput>) => {
    return await updateOrder(assemblingDetails)
  }, [ updateOrder ])

  const resetPaymentToken = useCallback(async () => {
    return await updateOrder({
      paymentToken: null
    })
  }, [ updateOrder ])

  /**
   * Here we will sync all pending changes before placing the order
   */
  const decoratedPlaceOrder = useCallback(async () => {
    let inSync = false

    // Store all non-saved changes
    if (Object.keys(pendingStateChanges).length > 0) {
      const response = await storeOrderChanges(pendingStateChanges)

      if (response?.isOk) {
        inSync = true
      }
    }
    else {
      inSync = true
    }

    // We run refetch to get the latest order data from backend
    const { data } = await refetch()

    if (!inSync) {
      console.warn('State is not fully synced with backend!')
    }

    return await placeOrderHandles.placeOrder(data.initializeOrder)
  }, [ pendingStateChanges, refetch, storeOrderChanges, placeOrderHandles ])

  return useMemo(() => ({
    pendingOrder,
    pendingOrderId,
    // skipped,
    loading,
    updateInProgress,
    updateAddress,
    updateSlot,
    updateRecipientInfo,
    updateCourierMessage,
    updateAssemblingDetails,
    resetPaymentToken,

    placeOrder: decoratedPlaceOrder,
    canPlaceOrder: placeOrderHandles.canPlaceOrder,
    placeOrderLoading: placeOrderHandles.loading,
    isOrderPlacedSuccessfully: placeOrderHandles.isOrderPlacedSuccessfully,
    errorCode: placeOrderHandles.errorCode,
    errorMessage: placeOrderHandles.errorMessage,
    errorShape: placeOrderHandles.errorShape
  }), [ pendingOrder, pendingOrderId, loading, updateInProgress, updateAddress, updateSlot, updateRecipientInfo, updateCourierMessage, updateAssemblingDetails, resetPaymentToken, decoratedPlaceOrder, placeOrderHandles.canPlaceOrder, placeOrderHandles.loading, placeOrderHandles.isOrderPlacedSuccessfully, placeOrderHandles.errorCode, placeOrderHandles.errorMessage, placeOrderHandles.errorShape ])
}